import { type ApolloError } from "@apollo/client";
import { useCallback, useRef, useState } from "react";

import {
  type ConfigMigrationStatusQuery,
  type StartConfigMigrationInput,
  useConfigMigrationStatusQuery,
  useStartConfigMigrationMutation,
} from "../../generated/graphql";

const POLL_INTERVAL_MS = 1500;
const TERMINAL_STATES = new Set(["completed", "failed"]);
/**
 * Stop polling after this many consecutive "job not found" responses. A job can
 * be evicted from BullMQ before reaching a terminal state; without this guard
 * the status query would poll forever.
 */
const MAX_MISSED_POLLS = 5;

/** The status payload for the active migration job, or null when none/unknown. */
export type ConfigMigrationStatusValue =
  ConfigMigrationStatusQuery["configMigrationStatus"];

/** Return shape of {@link useConfigMigration}. */
export type UseConfigMigration = {
  /** Starts a migration and begins polling its status. */
  start: (input: StartConfigMigrationInput) => Promise<void>;
  /** True while the start mutation is in flight. */
  starting: boolean;
  /** Error from the start mutation, if any. */
  startError?: ApolloError;
  /** The current job id, or null before the first run. */
  jobId: string | null;
  /** Latest polled status for the current job. */
  status: ConfigMigrationStatusValue;
};

/**
 * Orchestrates a config migration from the UI: enqueues the job, then polls its
 * status until the job reaches a terminal state (`completed`/`failed`) or is no
 * longer found (evicted), at which point polling stops. A start failure is
 * surfaced via `startError` and never rejects the returned promise, so a
 * fire-and-forget caller cannot produce an unhandled rejection.
 *
 * @returns The start action and the current job/status state.
 */
export function useConfigMigration(): UseConfigMigration {
  const [jobId, setJobId] = useState<string | null>(null);
  const missedPollsRef = useRef(0);
  const [startMutation, { loading: starting, error: startError }] =
    useStartConfigMigrationMutation();

  const { data, startPolling, stopPolling } = useConfigMigrationStatusQuery({
    variables: { jobId: jobId ?? "" },
    skip: jobId === null,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      const polled = result.configMigrationStatus ?? null;
      if (polled === null) {
        missedPollsRef.current += 1;
        if (missedPollsRef.current >= MAX_MISSED_POLLS) {
          stopPolling();
        }
        return;
      }
      missedPollsRef.current = 0;
      if (TERMINAL_STATES.has(polled.state)) {
        stopPolling();
      }
    },
  });

  const status = data?.configMigrationStatus ?? null;

  const start = useCallback(
    async (input: StartConfigMigrationInput) => {
      try {
        missedPollsRef.current = 0;
        setJobId(null);
        const result = await startMutation({ variables: { input } });
        const newJobId = result.data?.startConfigMigration ?? null;
        setJobId(newJobId);
        if (newJobId !== null) {
          startPolling(POLL_INTERVAL_MS);
        }
      } catch {
        // The mutation error is surfaced via `startError`; swallow it here so a
        // fire-and-forget caller does not see an unhandled rejection.
      }
    },
    [startMutation, startPolling],
  );

  return { start, starting, startError, jobId, status };
}
